import React, { FC, PropsWithChildren, useCallback } from 'react'
import { useController, useForm } from 'react-hook-form'
import cx from 'classnames'
import { track } from 'utils/analyticsV2'
import CoreInput from 'components/Core/CoreInput'
import { VendorScreeningStep, VendorScreeningSubmitData } from './types'
import NewLayout from './components/NewLayout'
import Spinner from 'svgs/spinner'

type SchedulerStepFields = {
  option: string
  why_not: string
}

type ScreeningStepProps = {
  submitting: boolean
  setDataToSubmit: (newDataToSubmit: null | VendorScreeningSubmitData) => void
  step: VendorScreeningStep
  stepIndex: number
  vendorScreeningResponses: any
  setVendorScreeningResponses: any
  onBack: () => void
}

const ScreeningStep: FC<PropsWithChildren<ScreeningStepProps>> = ({
  children,
  submitting,
  setDataToSubmit,
  step,
  stepIndex,
  vendorScreeningResponses,
  setVendorScreeningResponses,
  onBack,
}) => {
  const {
    control,
    register,
    handleSubmit: handleSubmitF,
  } = useForm<SchedulerStepFields>({
    disabled: submitting,
    defaultValues: {
      option: (vendorScreeningResponses[stepIndex] || {}).a,
    },
  })

  const { field: optionField } = useController({ name: 'option', control })

  const handleSubmit = useCallback(
    (data: SchedulerStepFields) => {
      const option = step.options.find((e) => e.label == data.option)
      if (step.options.length > 0 && !option) return

      track('click', { family: 'vendor-site-visit', code: 'submit', feature: 'screening', content_text: step.q })

      const newVendorScreeningResponse = {
        outcome: option ? (option.ok ? 'ok' : step.negativeOutcome) : 'ok',
        q: step.q,
        a: option ? option.label : undefined,
        why_not: data.why_not || undefined,
      }
      const newVendorScreeningResponses = [...vendorScreeningResponses.slice(0, stepIndex), newVendorScreeningResponse]
      setVendorScreeningResponses(newVendorScreeningResponses)

      const newDataToSubmit = { vendor_screening_responses: newVendorScreeningResponses }
      setDataToSubmit(newDataToSubmit)
    },
    [setDataToSubmit, setVendorScreeningResponses, step, stepIndex, vendorScreeningResponses]
  )

  return (
    <form onSubmit={handleSubmitF(handleSubmit)}>
      <NewLayout.Outer>
        <NewLayout.Inner>
          <h1 className="tw:font-bold tw:tracking-tight tw:text-xl tw:leading-tight">{step.q}</h1>

          {children}

          <div className="tw:divide-y">
            {step.options.map((option, index) => (
              <div
                key={option.label}
                className={cx({ 'tw:pb-3': index < step.options.length - 1, 'tw:pt-3': index > 0 })}
              >
                <CoreInput.Radio
                  id={option.label}
                  label={option.label}
                  checked={optionField.value == option.label}
                  {...optionField}
                  value={option.label}
                />
                {optionField.value == option.label && !option.ok && (
                  <CoreInput.TextArea label="Why not?" className="tw:pt-3" {...register('why_not')} />
                )}
              </div>
            ))}
          </div>
        </NewLayout.Inner>
        <div className="tw:flex tw:gap-4 tw:items-center">
          <button
            type="button"
            disabled={submitting}
            className="tw:flex tw:items-center tw:justify-center tw:bg-white tw:rounded-full tw:text-sm tw:font-bold tw:py-4 tw:px-6 tw:min-w-56 tw:mb-12 tw:leading-[1rem]"
            onClick={onBack}
          >
            {'Back'}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="tw:flex tw:items-center tw:justify-center tw:bg-neutrals-1000 tw:text-white tw:rounded-full tw:text-sm tw:font-bold tw:py-4 tw:px-6 tw:min-w-56 tw:mb-12 tw:leading-[1rem]"
          >
            {submitting ? <Spinner strokeWidth={16} className="tw:size-4" /> : 'Continue'}
          </button>
        </div>
      </NewLayout.Outer>
    </form>
  )
}

export default ScreeningStep
